import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X } from 'lucide-react'

import { dealPartyService, slotOccupiedOf, type SlotOccupied } from '@/services/dealParties'
import { Field } from './fields'
import { detailOf } from './errors'
import { usePartyDialogFocus } from './usePartyDialogFocus'

/**
 * Add a party to a deal.
 *
 * The button existed and did nothing — no handler, no dialog. So a deal whose
 * guarantor or counsel nobody had recorded had no way to record them short of
 * editing the deal record somewhere else and hoping the Parties tab caught up.
 *
 * What this creates is a CRM contact linked to this deal — the same kind of row
 * capture creates off the correspondence — never a broker or sponsor MASTER.
 * Establishing a master is an operator decision made in the CRM, and no
 * automatic path in this platform has ever taken it; a dialog on a deal screen
 * is not the place to start.
 *
 * Where the slot is one the loan row has a column for, the name is written
 * there too, with lineage, so the pipeline grid agrees with this page.
 */
const SLOTS: { value: string; label: string; note: string }[] = [
  { value: 'sponsor', label: 'Sponsor / Borrower', note: 'Writes the deal’s sponsor and borrower fields' },
  { value: 'guarantor', label: 'Guarantor', note: 'Writes the deal’s guarantor field' },
  { value: 'principal', label: 'Principal', note: 'Writes the deal’s principal field' },
  { value: 'broker', label: 'Broker', note: 'Writes the deal’s broker field' },
  { value: 'lender', label: 'Lender / investor', note: 'Writes the deal’s lender and investor fields' },
  { value: '', label: 'Other party', note: 'Recorded on the CRM contact only — the deal row has no field for it' },
]

export function AddPartyDialog({
  loanId,
  onClose,
  onSaved,
}: {
  loanId: string
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  usePartyDialogFocus('Add party')
  const [name, setName] = useState('')
  const [slot, setSlot] = useState('')
  const [kind, setKind] = useState('')
  const [company, setCompany] = useState('')
  const [title, setTitle] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [website, setWebsite] = useState('')
  const [address, setAddress] = useState('')
  const [notes, setNotes] = useState('')
  const [isEntity, setIsEntity] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The slot already names someone. Adding replaced them silently and the
  // trail recorded the prior value as null (PTY-09); now the replacement is
  // asked for, and the server records who was replaced.
  const [occupied, setOccupied] = useState<SlotOccupied | null>(null)

  const chosen = SLOTS.find((option) => option.value === slot) ?? SLOTS[SLOTS.length - 1]

  const create = useMutation({
    mutationFn: (replace: boolean) =>
      dealPartyService.add(loanId, {
        replace,
        name: name.trim(),
        slot: slot || undefined,
        // How they appear on THIS deal. For a slot the deal names, the slot's
        // own label says it; for anybody else, whatever the person types.
        kind: (kind.trim() || (slot ? chosen.label : '')) || undefined,
        company: company.trim() || undefined,
        title: title.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        website: website.trim() || undefined,
        address: address.trim() || undefined,
        notes: notes.trim() || undefined,
        // The box says "this is an organisation"; left unticked it says
        // nothing. Sent as `false`, the server pinned it as a person's own
        // typing, so "FOUR CORNERS HOLDINGS LLC" added as guarantor became a
        // second card typed Person beside the sponsor firm's own.
        isEntity: isEntity || undefined,
      }),
    onSuccess: () => onSaved(),
    onError: (failure) => {
      const slotTaken = slotOccupiedOf(failure)
      if (slotTaken) {
        setOccupied(slotTaken)
        return
      }
      setError(detailOf(failure, 'The party could not be added.'))
    },
  })

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add party"
    >
      <div className="card flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-400/10 px-4 py-2.5">
          <span className="terminal-kicker">Add party</span>
          <button type="button" onClick={onClose} aria-label="Close" className="text-stone-500 hover:text-stone-200">
            <X size={14} />
          </button>
        </div>

        <form
          className="min-h-0 flex-1 space-y-3 overflow-y-auto scrollbar-thin px-4 py-3"
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            setOccupied(null)
            if (name.trim()) create.mutate(false)
          }}
        >
          <Field label="Name" required value={name} onChange={setName} autoFocus />

          <div>
            <label htmlFor="party-slot" className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-stone-600">
              Role on this deal
            </label>
            <select
              id="party-slot"
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              className="w-full rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1.5 text-[12px] text-stone-200 outline-none focus:border-amber-400/30"
            >
              {SLOTS.map((option) => (
                <option key={option.label} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[9px] text-stone-600">{chosen.note}</p>
          </div>

          {!slot && (
            <Field
              label="Capacity"
              value={kind}
              onChange={setKind}
              placeholder="Counsel, appraiser, co-broker…"
            />
          )}

          <Field label={isEntity ? 'Entity name' : 'Firm'} value={company} onChange={setCompany} />
          <Field label={isEntity ? 'Entity type' : 'Title'} value={title} onChange={setTitle} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Email" value={email} onChange={setEmail} type="email" />
            <Field label="Phone" value={phone} onChange={setPhone} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Website" value={website} onChange={setWebsite} placeholder="hollis-cap.com" />
            <Field label="Address" value={address} onChange={setAddress} />
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-[11px] text-stone-300">
            <input
              type="checkbox"
              checked={isEntity}
              onChange={(e) => setIsEntity(e.target.checked)}
              className="accent-amber-400"
            />
            This is an organization, not a person
          </label>

          <Field label="Notes" value={notes} onChange={setNotes} />

          <p className="text-[9px] leading-relaxed text-stone-600">
            Recorded as a CRM contact linked to this deal. It will appear in the contact directory
            alongside the people captured off this deal’s correspondence.
          </p>

          {occupied && (
            <div role="alert" className="rounded border border-amber-400/30 bg-amber-400/[0.06] px-2.5 py-2 text-[11px] text-amber-100">
              {(occupied.changes?.length ?? 0) > 1 ||
              (occupied.replaces && occupied.replaces !== occupied.current) ? (
                // One part of a compound, or a mirrored borrower moving with
                // the sponsor: the server's sentence names every field.
                <p>{occupied.message} The replaced name stays in the audit trail.</p>
              ) : (
                <p>
                  This deal&rsquo;s {chosen.label} is already <strong>{occupied.current}</strong>. Adding{' '}
                  {name.trim()} replaces that name on the deal; the replaced name stays in the audit trail.
                </p>
              )}
              <div className="mt-2 flex justify-end gap-2">
                <button type="button" onClick={() => setOccupied(null)} className="btn-secondary text-[11px]">
                  Keep {occupied.current}
                </button>
                <button
                  type="button"
                  onClick={() => create.mutate(true)}
                  disabled={create.isPending}
                  className="rounded-md border border-amber-400/30 bg-amber-400/[0.12] px-3 py-1 text-[11px] font-medium text-amber-200 disabled:opacity-40"
                >
                  Replace with {name.trim()}
                </button>
              </div>
            </div>
          )}

          {error && <p className="text-[11px] text-red-300">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary text-[11px]">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || create.isPending}
              className="rounded-md border border-amber-400/20 bg-amber-400/[0.08] px-3 py-1 text-[11px] font-medium text-amber-200 transition-colors hover:bg-amber-400/[0.14] disabled:cursor-not-allowed disabled:text-stone-600"
            >
              {create.isPending ? 'Adding…' : 'Add party'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
